import React, { useState, useEffect } from 'react';
import { isLink, inline, Markdown, HexInput, SWATCHES } from './discordMarkdown';

// The dashboard's Embed tab (classic embed): build a message with an embed, see it as Discord will show it, and have
// TOGΞ send it. The draft is kept in this browser, so leaving the tab doesn't lose it.

const DRAFT_KEY = 'dashboardEmbedDraft';
const EMPTY = {
  content: '',
  embed: {
    color: '#5865f2', title: '', url: '', description: '',
    author: { name: '', url: '', icon: '' },
    fields: [], image: '', thumbnail: '',
    footer: { text: '', icon: '' }, timestamp: false,
  },
  buttons: [],
};

const loadDraft = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(DRAFT_KEY));
    if (saved?.embed) {
      const embed = { ...EMPTY.embed, ...saved.embed };
      if (!/^#[0-9a-f]{6}$/i.test(embed.color)) embed.color = EMPTY.embed.color; // a half-typed colour from before
      return { ...EMPTY, ...saved, embed };
    }
  } catch {}
  return EMPTY;
};

// ── The preview: drawn to match Discord's dark theme ─────────────────
const Preview = ({ draft, bot, ctx }) => {
  const e = draft.embed;
  const hasEmbed = e.title || e.description || e.author.name || e.fields.some(f => f.name || f.value) || isLink(e.image)
    || isLink(e.thumbnail) || e.footer.text;
  const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return (
    <div className="rounded-xl bg-[#313338] p-4 text-[#dbdee1] text-[15px] leading-[1.375rem]" style={{ fontFamily: 'gg sans, "Noto Sans", Helvetica, Arial, sans-serif' }}>
      <div className="flex gap-4">
        {bot?.avatar ? <img src={bot.avatar} alt="" className="h-10 w-10 rounded-full flex-shrink-0" /> : <div className="h-10 w-10 rounded-full bg-[#5865f2] flex-shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-white">{bot?.name || 'TOGΞ'}</span>
            <span className="text-[10px] font-semibold px-1 rounded bg-[#5865f2] text-white leading-4">APP</span>
            <span className="text-xs text-[#949ba4]">Today at {time}</span>
          </div>
          {draft.content && <Markdown text={draft.content} ctx={ctx} />}
          {!draft.content && !hasEmbed && <p className="text-[#949ba4] italic text-sm">Your message will show here as you type.</p>}

          {hasEmbed && (
            <div className="mt-1 max-w-[520px] rounded bg-[#2b2d31] border-l-4 grid" style={{ borderLeftColor: e.color, gridTemplateColumns: isLink(e.thumbnail) ? '1fr 80px' : '1fr' }}>
              <div className="min-w-0 px-4 pt-2 pb-4 pr-4" style={{ gridColumn: 1 }}>
                {e.author.name && (
                  <div className="flex items-center gap-2 mt-2">
                    {isLink(e.author.icon) && <img src={e.author.icon} alt="" className="h-6 w-6 rounded-full" />}
                    <span className="text-sm font-semibold text-white">{e.author.name}</span>
                  </div>
                )}
                {e.title && (
                  <div className={`mt-2 font-semibold ${isLink(e.url) ? 'text-[#00a8fc]' : 'text-white'}`}>{inline(e.title, ctx)}</div>
                )}
                {e.description && <Markdown text={e.description} ctx={ctx} className="mt-2 text-sm" />}
                {e.fields.some(f => f.name || f.value) && (
                  <div className="mt-2 grid grid-cols-3 gap-x-2 gap-y-2">
                    {e.fields.filter(f => f.name || f.value).map((f, i) => (
                      <div key={i} className={`min-w-0 text-sm ${f.inline ? '' : 'col-span-3'}`}>
                        <div className="font-semibold text-white">{inline(f.name, ctx) || ' '}</div>
                        <Markdown text={f.value} ctx={ctx} />
                      </div>
                    ))}
                  </div>
                )}
                {isLink(e.image) && <img src={e.image} alt="" className="mt-4 rounded max-h-[300px] max-w-full object-contain" />}
                {(e.footer.text || e.timestamp) && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-[#dbdee1]">
                    {isLink(e.footer.icon) && e.footer.text && <img src={e.footer.icon} alt="" className="h-5 w-5 rounded-full" />}
                    <span>{e.footer.text}{e.footer.text && e.timestamp ? ' • ' : ''}{e.timestamp ? `Today at ${time}` : ''}</span>
                  </div>
                )}
              </div>
              {isLink(e.thumbnail) && (
                <img src={e.thumbnail} alt="" className="mt-4 mr-4 h-20 w-20 rounded object-cover" style={{ gridColumn: 2 }} />
              )}
            </div>
          )}

          {draft.buttons.some(b => b.label && isLink(b.url)) && (
            <div className="mt-2 flex flex-wrap gap-2">
              {draft.buttons.filter(b => b.label && isLink(b.url)).map((b, i) => (
                <a key={i} href={b.url} target="_blank" rel="noreferrer" className="px-4 py-1.5 rounded bg-[#4e5058] hover:bg-[#6d6f78] text-white text-sm font-medium flex items-center gap-1.5">
                  {b.label} <span className="text-xs">↗</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── The form ─────────────────────────────────────────────────────────
const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="flex items-baseline justify-between text-zinc-400 text-[11px] mb-1">
      <span>{label}</span>{hint && <span className="text-zinc-600">{hint}</span>}
    </span>
    {children}
  </label>
);
const inputClass = 'w-full rounded-md bg-zinc-950 border border-zinc-700 px-2.5 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-500';
const Section = ({ title, children, right }) => (
  <section className="rounded-xl border border-zinc-800 bg-zinc-900/80 p-3 space-y-2.5">
    <div className="flex items-center justify-between">
      <h3 className="text-zinc-400 text-[11px] font-semibold uppercase tracking-wider">{title}</h3>
      {right}
    </div>
    {children}
  </section>
);
const Small = ({ children, onClick, disabled, danger }) => (
  <button type="button" onClick={onClick} disabled={disabled}
    className={`px-2 py-1 rounded-md text-[11px] font-semibold disabled:opacity-40 ${danger ? 'text-red-400 hover:bg-red-900/30' : 'bg-zinc-800 hover:bg-zinc-700 text-white'}`}>
    {children}
  </button>
);

export default function EmbedBuilder({ ask, lists, bot, channelOptions }) {
  const [draft, setDraft] = useState(loadDraft);
  const [channel, setChannel] = useState('');
  const [ping, setPing] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { ok, text, url }
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(draft)); } catch {}
  }, [draft]);

  const e = draft.embed;
  const setEmbed = (changes) => setDraft(d => ({ ...d, embed: { ...d.embed, ...changes } }));
  const setPart = (part, changes) => setDraft(d => ({ ...d, embed: { ...d.embed, [part]: { ...d.embed[part], ...changes } } }));
  const setFieldAt = (i, changes) => setEmbed({ fields: e.fields.map((f, j) => (j === i ? { ...f, ...changes } : f)) });
  const moveField = (i, by) => {
    const fields = [...e.fields];
    const [f] = fields.splice(i, 1);
    fields.splice(i + by, 0, f);
    setEmbed({ fields });
  };
  const setButtonAt = (i, changes) => setDraft(d => ({ ...d, buttons: d.buttons.map((b, j) => (j === i ? { ...b, ...changes } : b)) }));

  // Discord counts the embed's text towards a 6,000 limit
  const total = [e.title, e.description, e.author.name, e.footer.text, ...e.fields.flatMap(f => [f.name, f.value])]
    .reduce((n, s) => n + (s || '').length, 0);
  const badLinks = [['Title link', e.url], ['Author link', e.author.url], ['Author icon', e.author.icon], ['Image', e.image],
    ['Thumbnail', e.thumbnail], ['Footer icon', e.footer.icon], ...draft.buttons.map((b, i) => [`Button ${i + 1} link`, b.url])]
    .filter(([, v]) => v && !isLink(v)).map(([name]) => name);
  const hasContent = draft.content.trim() || total > 0 || isLink(e.image) || isLink(e.thumbnail);
  const pings = /@everyone|@here|<@&\d+>/.test(draft.content);

  const send = async () => {
    setSending(true); setResult(null);
    const answer = await ask('embed.send', { channel, content: draft.content, embed: draft.embed, buttons: draft.buttons, ping });
    setSending(false);
    if (answer.ok) setResult({ ok: true, text: answer.data.message, url: answer.data.url });
    else setResult({ ok: false, text: answer.error === 'timeout' ? "The bot didn't answer." : answer.error === 'login' ? 'Your login ran out: refresh and log in again.' : answer.error });
  };

  const ctx = { channels: lists.channels, roles: lists.roles };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
      {/* Form */}
      <div className="space-y-3">
        <Section title="Message">
          <Field label="Text above the embed" hint={`${draft.content.length}/2000`}>
            <textarea value={draft.content} maxLength={2000} rows={2} onChange={ev => setDraft(d => ({ ...d, content: ev.target.value }))}
              placeholder="Optional. Markdown works: **bold**, # Heading, @everyone…" className={`${inputClass} resize-y`} />
          </Field>
        </Section>

        <Section title="Embed" right={<span className={`text-[10px] ${total > 6000 ? 'text-red-400' : 'text-zinc-600'}`}>{total.toLocaleString()}/6,000</span>}>
          <HexInput value={e.color} onChange={color => setEmbed({ color })} swatches={SWATCHES} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Field label="Author name"><input value={e.author.name} maxLength={256} onChange={ev => setPart('author', { name: ev.target.value })} className={inputClass} /></Field>
            <Field label="Author link"><input value={e.author.url} onChange={ev => setPart('author', { url: ev.target.value })} placeholder="https://…" className={inputClass} /></Field>
            <Field label="Author icon"><input value={e.author.icon} onChange={ev => setPart('author', { icon: ev.target.value })} placeholder="Image link" className={inputClass} /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="sm:col-span-2"><Field label="Title" hint={`${e.title.length}/256`}><input value={e.title} maxLength={256} onChange={ev => setEmbed({ title: ev.target.value })} className={inputClass} /></Field></div>
            <Field label="Title link"><input value={e.url} onChange={ev => setEmbed({ url: ev.target.value })} placeholder="https://…" className={inputClass} /></Field>
          </div>
          <Field label="Description" hint={`${e.description.length}/4096`}>
            <textarea value={e.description} maxLength={4096} rows={5} onChange={ev => setEmbed({ description: ev.target.value })}
              placeholder="**bold**, *italic*, __underline__, [links](https://…), # Headings, > quotes" className={`${inputClass} resize-y`} />
          </Field>
        </Section>

        <Section title={`Fields · ${e.fields.length}/25`} right={
          <Small onClick={() => setEmbed({ fields: [...e.fields, { name: '', value: '', inline: true }] })} disabled={e.fields.length >= 25}>+ Add field</Small>
        }>
          {e.fields.length === 0 && <p className="text-zinc-600 text-[11px]">Fields are small titled blocks, up to 3 side by side when “inline”.</p>}
          {e.fields.map((f, i) => (
            <div key={i} className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-2 space-y-1.5">
              <div className="flex gap-2">
                <input value={f.name} maxLength={256} onChange={ev => setFieldAt(i, { name: ev.target.value })} placeholder="Field name" className={inputClass} />
                <label className="flex items-center gap-1 text-[11px] text-zinc-400 whitespace-nowrap">
                  <input type="checkbox" checked={f.inline} onChange={ev => setFieldAt(i, { inline: ev.target.checked })} /> Inline
                </label>
              </div>
              <textarea value={f.value} maxLength={1024} rows={2} onChange={ev => setFieldAt(i, { value: ev.target.value })} placeholder="Field text" className={`${inputClass} resize-y`} />
              <div className="flex gap-1 justify-end">
                <Small onClick={() => moveField(i, -1)} disabled={i === 0}>↑</Small>
                <Small onClick={() => moveField(i, 1)} disabled={i === e.fields.length - 1}>↓</Small>
                <Small danger onClick={() => setEmbed({ fields: e.fields.filter((_, j) => j !== i) })}>Remove</Small>
              </div>
            </div>
          ))}
        </Section>

        <Section title="Pictures and footer">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Field label="Big image"><input value={e.image} onChange={ev => setEmbed({ image: ev.target.value })} placeholder="Image link" className={inputClass} /></Field>
            <Field label="Thumbnail (top right)"><input value={e.thumbnail} onChange={ev => setEmbed({ thumbnail: ev.target.value })} placeholder="Image link" className={inputClass} /></Field>
            <Field label="Footer text" hint={`${e.footer.text.length}/2048`}><input value={e.footer.text} maxLength={2048} onChange={ev => setPart('footer', { text: ev.target.value })} placeholder="e.g. CHΞΞKSCORD" className={inputClass} /></Field>
            <Field label="Footer icon"><input value={e.footer.icon} onChange={ev => setPart('footer', { icon: ev.target.value })} placeholder="Image link" className={inputClass} /></Field>
          </div>
          <label className="flex items-center gap-2 text-[11px] text-zinc-400">
            <input type="checkbox" checked={e.timestamp} onChange={ev => setEmbed({ timestamp: ev.target.checked })} /> Show the time it was sent, in the footer
          </label>
        </Section>

        <Section title={`Link buttons · ${draft.buttons.length}/5`} right={
          <Small onClick={() => setDraft(d => ({ ...d, buttons: [...d.buttons, { label: '', url: '' }] }))} disabled={draft.buttons.length >= 5}>+ Add button</Small>
        }>
          {draft.buttons.length === 0 && <p className="text-zinc-600 text-[11px]">Buttons under the message that open a link.</p>}
          {draft.buttons.map((b, i) => (
            <div key={i} className="flex gap-2">
              <input value={b.label} maxLength={80} onChange={ev => setButtonAt(i, { label: ev.target.value })} placeholder="Label" className={`${inputClass} !w-1/3`} />
              <input value={b.url} onChange={ev => setButtonAt(i, { url: ev.target.value })} placeholder="https://…" className={inputClass} />
              <Small danger onClick={() => setDraft(d => ({ ...d, buttons: d.buttons.filter((_, j) => j !== i) }))}>✕</Small>
            </div>
          ))}
        </Section>
      </div>

      {/* Preview and send: stays in view while scrolling the form */}
      <div className="space-y-3 lg:sticky lg:top-4">
        <Preview draft={draft} bot={bot} ctx={ctx} />

        <section className="rounded-xl border border-zinc-800 bg-zinc-900/80 p-3 space-y-2.5">
          {badLinks.length > 0 && (
            <p className="text-amber-400 text-[11px]">These need to be full links starting https://, or they'll be left out: {badLinks.join(', ')}</p>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <select value={channel} onChange={ev => setChannel(ev.target.value)}
              className="flex-1 rounded-md bg-zinc-950 border border-zinc-700 px-2 py-1.5 text-xs text-white focus:outline-none focus:border-zinc-500">
              <option value="">Send to which channel?</option>
              {channelOptions()}
            </select>
            <button type="button" onClick={send} disabled={!channel || !hasContent || sending || total > 6000}
              className="px-4 py-1.5 rounded-md bg-[#5865f2] hover:bg-[#4752c4] text-white text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
          <div className="flex items-center justify-between gap-2">
            <label className={`flex items-center gap-2 text-[11px] ${pings ? 'text-zinc-300' : 'text-zinc-600'}`}>
              <input type="checkbox" checked={ping} onChange={ev => setPing(ev.target.checked)} disabled={!pings} />
              Let @everyone, @here and role mentions ping people
            </label>
            <button type="button" onClick={() => { if (!confirmReset) { setConfirmReset(true); return; } setDraft(EMPTY); setConfirmReset(false); setResult(null); }}
              onBlur={() => setConfirmReset(false)}
              className={`text-[11px] font-semibold px-2 py-1 rounded-md ${confirmReset ? 'bg-red-600 text-white' : 'text-zinc-500 hover:text-white'}`}>
              {confirmReset ? 'Clear everything?' : 'Start over'}
            </button>
          </div>
          {result && (
            <p className={`text-[11px] ${result.ok ? 'text-green-300' : 'text-red-300'}`}>
              {result.text}{' '}
              {result.url && <a href={result.url} target="_blank" rel="noreferrer" className="underline">Open it in Discord ↗</a>}
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
